import { Component, model, OnInit, signal } from '@angular/core';
import { BnLoadingService, EntityOrderedRequestParameters, EntityPagedRequestParameters, EntityPagedResult } from '@bndes/bndes-ux4/bndes-angular-utils';
import { CicloDistribuicaoDto } from 'src/app/model/ciclo-distribuicao-dto.model';
import { BnModalMessageService } from '@bndes/bndes-ux4/bn-modal-message';
import { DistribuicaoLimitesService } from '../distribuicao-limites.service';
import { BnAlertsService } from '@bndes/bndes-ux4/bn-alerts';
import { TuplaEditavel } from 'src/app/model/tupla-editavel.model';
import { ResultadoPaginado } from 'src/app/model/resultado';
import { CommonModule, DatePipe } from '@angular/common';
import { BnMaskPipe } from '@bndes/bndes-ux4/bn-mask/bn-mask-pipe';
import { BnPaginacaoComponent } from '@bndes/bndes-ux4/bn-paginacao';
import { BnOrdenacaoComponent } from '@bndes/bndes-ux4/bn-ordenacao';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { inject, output } from '@angular/core';
import { AmbienteService } from 'src/app/shared/services/ambiente-service';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-distribuicao-limites-configuracao',
  imports: [
    BnMaskPipe,
    DatePipe,
    BnPaginacaoComponent,
    BnOrdenacaoComponent,
    RouterLink,
    FormsModule,
    CommonModule,
    NgbTooltip
  ],
  templateUrl: './distribuicao-limites-configuracao.component.html',
  styleUrl: './distribuicao-limites-configuracao.component.scss',
  standalone: true
})
export class DistribuicaoLimitesConfiguracaoComponent implements OnInit{

  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(DistribuicaoLimitesService);
  private readonly modalService = inject(BnModalMessageService);
  private readonly loadingService = inject(BnLoadingService);
  private readonly alertsService = inject(BnAlertsService);
  private readonly ambienteService = inject(AmbienteService);

  public ambientePrd = signal<boolean>(true);
  public opcoesPaginacao: number[] = [10, 15, 20];
  public atributosValidosOrdenacao: string[] = [
    "numero", "numeroTranche", "dataInicio", "dataFim",
    "valorAporte", "ignoraMetas"
  ];

  public ciclosPaginados: TuplaEditavel<CicloDistribuicaoDto>[] = [];

  public carregandoCiclos = false;

  public atributosOrdenacao!: EntityOrderedRequestParameters;
  public atributosPaginacao = model<EntityPagedResult<CicloDistribuicaoDto>>({
    page: 1,
    pageSize: this.opcoesPaginacao[0],
    pages: 1,
    count: 0,
    list: []
  });
  public aoMudarPagina = output<EntityPagedRequestParameters>();


  ngOnInit() {
    this.ambienteService.isPRD().subscribe({
      next: ((isPRD) => this.ambientePrd.set(isPRD))
    });
    const atributosPaginacao = this.atributosPaginacao();
    const page = Number(
      this.route.snapshot.queryParamMap.get('page')
      ?? atributosPaginacao.page ?? 1
    );
    let pageSize = Number(
      this.route.snapshot.queryParamMap.get('pageSize')
      ?? atributosPaginacao.pageSize ?? this.opcoesPaginacao[0]
    );
    let atributoOrdenacao = this.route.snapshot.queryParamMap.get('atributoOrdenacao') ?? 'numero';
    const sentidoOrdenacao = this.route.snapshot.queryParamMap.get('sentidoOrdenacao') ?? 'asc';
    
    if (!this.atributosValidosOrdenacao.includes(atributoOrdenacao)) {
      atributoOrdenacao = "numero";
    };
    if (!this.opcoesPaginacao.includes(pageSize)){
      pageSize = this.opcoesPaginacao[0];
    }
  
    this.atributosPaginacao.update((p) => ({
      ...p,
      page,
      pageSize
    }))
    this.atributosOrdenacao = {
      atributo: atributoOrdenacao,
      sentido: sentidoOrdenacao.toLocaleLowerCase() == "asc" ? "asc" : "desc"
    }
    this.buscar();
  }

  public buscar() {
    this.loadingService.bloqueiaTela();
    const params: any = {};
    
    params.pagina = this.atributosPaginacao().page;
    params.limite = this.atributosPaginacao().pageSize;
    params.ordemAtributo = this.atributosOrdenacao.atributo;
    params.ordemSentido = this.atributosOrdenacao.sentido;

    this.service.buscarCiclosPaginados(params).subscribe({
      next: (response: ResultadoPaginado<CicloDistribuicaoDto>) => {
        this.ciclosPaginados = response.resultados.map((ciclo) => new TuplaEditavel(ciclo));
        this.atributosPaginacao.update(p => {
          return {
          ...p,
          count: response.totalRegistrosSemPaginacao,
          list: response.resultados,
          pages: Math.ceil(
            response.totalRegistrosSemPaginacao / p.pageSize
          )
        }})
        this.loadingService.liberaTela();
      }, 
      error: err => {
        console.error(err);
        this.loadingService.liberaTela();
      }
    })
  }

  mudancaPagina(novaPagina: EntityPagedResult<CicloDistribuicaoDto>) {
    this.atributosPaginacao.update(() => novaPagina);
    this.buscar();
  }

  mudancaOrdenacao(novaOrdenacao: EntityOrderedRequestParameters) {
    this.atributosOrdenacao = novaOrdenacao;
    this.buscar();
  }

  permitirEdicao(event: MouseEvent, tupla: TuplaEditavel<CicloDistribuicaoDto>) {
    if (this.podeEditar(tupla))
      tupla.permiteEdicao = !tupla.permiteEdicao;
    event.stopPropagation();
  }

  reverter(event: MouseEvent, tupla: TuplaEditavel<CicloDistribuicaoDto>) {
    if (this.ambientePrd()) {
      this.alertsService.alertaErro("Erro", "Não é possível reverter ciclo em produção");
      return;
    }
    
    this.abreModalConfirmacao(`Você deseja reverter o ciclo ${tupla.objeto.numero}?`)
    .then((resp) => {
      if (resp === true) {
        this.loadingService.bloqueiaTela();
        this.service.reverterCicloPorId(tupla.objeto.id).subscribe({
          next: (_msg) => {
            this.loadingService.liberaTela();
            this.alertsService.alertaSucesso('Sucesso', 'Ciclo retornado com sucesso.');
            this.buscar();
          }, 
          error: err => {
            this.alertsService.alertaErro("Erro", "Erro ao reverter ciclo.");
            console.error(err);
            this.loadingService.liberaTela();
          }
        })
      }
    });
    event.stopPropagation();
  }


  pedirConfirmacao(tupla: TuplaEditavel<CicloDistribuicaoDto>) {
    const ciclo = tupla.objeto;
    const valorAnterior = !ciclo.ignoraMetas;
    this.abreModalConfirmacao(`Você deseja confirmar a alteração no ciclo ${ciclo.numero}?`)
    .then((resp) => {
      if (resp === true) {
        this.loadingService.bloqueiaTela();
        this.service
          .update(ciclo)
          .subscribe(
            {
              next: (msg: CicloDistribuicaoDto) => {
                tupla.permiteEdicao = false;
                tupla.objeto = msg;
                this.loadingService.liberaTela()
              },
              error: (error) => {
                this.alertsService.alertaErro("Erro", "Não foi possível atualizar ciclo");
                console.error(error);
                ciclo.ignoraMetas = valorAnterior;
                this.loadingService.liberaTela()
              },
              complete: () => {
                this.loadingService.liberaTela()
              }
            }
          );
      } else {
        ciclo.ignoraMetas = valorAnterior;
      }
    });
  }

  async abreModalConfirmacao(mensagem: string): Promise<boolean> {
    const titulo = 'Confirmação';

    try {
      const resultadoConfimacao = await this.modalService.abrirConfirm(titulo, mensagem).result;
      return resultadoConfimacao.value === 'ok';
    } catch (e) {
      return false;
    }
  }

  podeEditar(tupla: TuplaEditavel<CicloDistribuicaoDto>): boolean {
    const data = tupla.objeto.dataFim;

    return !this.dataPassou(data);
  }

  dataPassou(data: Date | string) {
    return new Date() > new Date(data);
  }

}
